<template>
  <div class="sd">
    <div class="sd-section-head">
      <h2>Delivery</h2>
      <p class="sd-help">
        Templates the team uses when sending a treatment plan link to a patient.
        Patients receive these messages with a tap-to-open link to their personalized
        plan view. Available placeholders:
        <code>&#123;patientFirstName&#125;</code>,
        <code>&#123;practiceName&#125;</code>,
        <code>&#123;planLink&#125;</code>.
        Your Email Signature (Settings → Email) is added below the email body automatically when
        it is switched on — don't paste it here.
      </p>
    </div>

    <div class="sd-card">
      <h3 class="sd-card-title">SMS</h3>
      <p class="sd-card-desc">
        Short text — keep it under ~160 characters so it fits in a single SMS. Patients only see
        the link if their phone supports tapping URLs (most modern phones do).
      </p>
      <div class="sd-field">
        <label>Message body</label>
        <textarea
          v-model="local.smsTemplate"
          class="sd-input sd-textarea"
          rows="3"
          placeholder="Hi {patientFirstName}, your treatment plan from {practiceName} is ready: {planLink}"
        ></textarea>
        <div class="sd-charcount" :class="{ warn: smsCharCount > 160 }">
          {{ smsCharCount }} / 160 chars
        </div>
      </div>
    </div>

    <div class="sd-card">
      <h3 class="sd-card-title">Email</h3>
      <p class="sd-card-desc">
        Sent from your practice's verified SendGrid sender address. Plain text body — line breaks
        are preserved; the &#123;planLink&#125; placeholder becomes a clickable link.
      </p>
      <div class="sd-field">
        <label>Subject</label>
        <input
          v-model="local.emailSubject"
          class="sd-input"
          placeholder="Your treatment plan from {practiceName}"
        />
      </div>
      <div class="sd-field">
        <label>Body</label>
        <textarea
          v-model="local.emailBody"
          class="sd-input sd-textarea"
          rows="9"
          placeholder="Email body..."
        ></textarea>
      </div>
    </div>

    <div class="sd-savebar">
      <span v-if="dirty" class="sd-dirty">Unsaved changes</span>
      <span v-else class="sd-clean">All changes saved</span>
      <span class="sd-spacer"></span>
      <button class="sd-btn" @click="onReset" :disabled="!dirty">Reset</button>
      <button class="sd-btn-primary" @click="onSave" :disabled="!dirty || saving">
        <i v-if="saving" class="fa fa-circle-o-notch fa-spin"></i>
        Save
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { TX_PRESENTER_DEFAULTS } from '@/pages/TxPresenter/constants/settingsDefaults.js';

const cloneDefaults = () => JSON.parse(JSON.stringify(TX_PRESENTER_DEFAULTS.delivery));

export default {
  name: 'SettingsDelivery',
  props: {
    loading: { type: Boolean, default: false },
    saving: { type: Boolean, default: false },
  },
  data() {
    return {
      local: cloneDefaults(),
      original: cloneDefaults(),
    };
  },
  computed: {
    ...mapGetters('txPresenter', ['settingsDeliveryResolved']),
    dirty() {
      return JSON.stringify(this.local) !== JSON.stringify(this.original);
    },
    smsCharCount() {
      return (this.local.smsTemplate || '').length;
    },
  },
  watch: {
    settingsDeliveryResolved: {
      immediate: true,
      handler(resolved) {
        if (!resolved) return;
        const merged = { ...cloneDefaults(), ...resolved };
        this.local = merged;
        this.original = JSON.parse(JSON.stringify(merged));
      },
    },
  },
  methods: {
    async onSave() {
      try {
        await this.$store.dispatch('txPresenter/saveSettingsSection', {
          section: 'delivery',
          value: this.local,
        });
        this.original = JSON.parse(JSON.stringify(this.local));
      } catch (e) { /* in state */ }
    },
    onReset() {
      this.local = JSON.parse(JSON.stringify(this.original));
    },
  },
};
</script>

<style scoped>
.sd {
  max-width: 720px;
  margin: 0 auto;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
}
.sd-section-head { margin-bottom: 22px; }
.sd-section-head h2 {
  margin: 0 0 4px;
  font-size: 22px;
  font-weight: 600;
  letter-spacing: -0.3px;
  color: #0f172a;
}
.sd-help {
  margin: 0;
  font-size: 13px;
  color: #64748b;
  line-height: 1.55;
}
.sd-help code {
  font-family: 'SFMono-Regular', Menlo, Monaco, monospace;
  font-size: 11.5px;
  background: #f1f3f6;
  padding: 1px 5px;
  border-radius: 3px;
  color: #0d5f54;
}

.sd-card {
  background: #fff;
  border: 1px solid #e4e7ec;
  border-radius: 10px;
  padding: 20px 22px;
  margin-bottom: 16px;
  box-shadow: 0 1px 2px rgba(15,23,42,0.04);
}
.sd-card-title {
  margin: 0 0 6px;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: #475569;
}
.sd-card-desc {
  margin: 0 0 14px;
  font-size: 12.5px;
  color: #64748b;
  line-height: 1.55;
}

.sd-field { margin-bottom: 14px; }
.sd-field:last-child { margin-bottom: 0; }
.sd-field > label {
  display: block;
  font-size: 12px;
  color: #475569;
  font-weight: 600;
  margin-bottom: 6px;
}
.sd-input,
.sd-textarea {
  display: block;
  width: 100%;
  padding: 8px 10px;
  font-size: 13px;
  border: 1px solid #e4e7ec;
  border-radius: 6px;
  background: #fff;
  color: #0f172a;
  font-family: inherit;
}
.sd-textarea {
  resize: vertical;
  min-height: 70px;
  line-height: 1.55;
}
.sd-input:focus,
.sd-textarea:focus {
  outline: none;
  border-color: #0d5f54;
  box-shadow: 0 0 0 3px rgba(13, 95, 84, 0.12);
}
.sd-charcount {
  margin-top: 4px;
  font-size: 11px;
  color: #94a3b8;
  text-align: right;
}
.sd-charcount.warn { color: #b45309; font-weight: 500; }

.sd-savebar {
  position: sticky;
  bottom: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(8px);
  border: 1px solid #e4e7ec;
  border-radius: 10px;
  margin-top: 24px;
  box-shadow: 0 4px 12px rgba(15,23,42,0.05);
}
.sd-dirty { font-size: 12px; color: #b45309; font-weight: 500; }
.sd-clean { font-size: 12px; color: #64748b; }
.sd-spacer { flex: 1; }
.sd-btn,
.sd-btn-primary {
  padding: 7px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid #e4e7ec;
  background: #fff;
  color: #1e293b;
}
.sd-btn:hover:not(:disabled) { background: #f1f3f6; }
.sd-btn:disabled,
.sd-btn-primary:disabled { opacity: 0.55; cursor: not-allowed; }
.sd-btn-primary {
  background: #0d5f54;
  border-color: #0d5f54;
  color: #fff;
}
.sd-btn-primary:hover:not(:disabled) { background: #094840; }
</style>
